export enum ListagemDeMovimentacoes {
  OFERTAS_PRIMARIAS = 'ofertas-primarias',
  COMPRAS_VENDAS = 'compras-vendas',
}

export interface PaginacaoDaListagem {
  pagina: number;
  tamanhoDaPagina: number;
}

export interface FiltroComPaginacao {
  page?: number;
  pageSize?: number;
}

const PREFIXO_DA_CHAVE = 'laqus:movimentacoes:paginacao-da-listagem';

const chaveDaListagem = (listagem: ListagemDeMovimentacoes): string => `${PREFIXO_DA_CHAVE}:${listagem}`;

const usarArmazenamentoLocal = <T>(operacao: (armazenamento: Storage) => T, valorQuandoIndisponivel: T): T => {
  try {
    const armazenamento = globalThis.localStorage;
    return armazenamento ? operacao(armazenamento) : valorQuandoIndisponivel;
  } catch {
    return valorQuandoIndisponivel;
  }
};

const ehPosicaoValida = (valor: unknown): valor is number => Number.isInteger(valor) && (valor as number) > 0;

const converterParaPaginacao = (valorArmazenado: string | null): PaginacaoDaListagem | undefined => {
  if (valorArmazenado === null) {
    return undefined;
  }

  try {
    const { pagina, tamanhoDaPagina } = JSON.parse(valorArmazenado) as Partial<PaginacaoDaListagem>;
    return ehPosicaoValida(pagina) && ehPosicaoValida(tamanhoDaPagina) ? { pagina, tamanhoDaPagina } : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Guarda a paginação que a listagem aplicou, para que a exportação repita a mesma fatia.
 * Deve ser chamada a cada consulta ao endpoint `/paginated`, substituindo o valor anterior
 * daquela listagem.
 *
 * Página e tamanho são gravados juntos porque só fazem sentido como par: reaproveitar o
 * tamanho de uma consulta com a página de outra deslocaria a fatia exportada. Valores
 * inválidos são descartados em vez de gravados.
 *
 * Cada listagem tem sua própria chave: a paginação de ofertas primárias não interfere na
 * de compras e vendas.
 */
export const registrarPaginacaoDaListagem = (
  listagem: ListagemDeMovimentacoes,
  paginacao: Partial<PaginacaoDaListagem>
): void => {
  if (!ehPosicaoValida(paginacao.pagina) || !ehPosicaoValida(paginacao.tamanhoDaPagina)) {
    return;
  }

  const paginacaoDaListagem: PaginacaoDaListagem = {
    pagina: paginacao.pagina,
    tamanhoDaPagina: paginacao.tamanhoDaPagina,
  };

  usarArmazenamentoLocal<void>(
    (armazenamento) => armazenamento.setItem(chaveDaListagem(listagem), JSON.stringify(paginacaoDaListagem)),
    undefined
  );
};

/**
 * Devolve a paginação da última listagem consultada, ou `undefined` quando não há valor
 * utilizável — nenhuma listagem consultada ainda, conteúdo corrompido ou armazenamento
 * local indisponível.
 */
export const obterPaginacaoDaListagem = (listagem: ListagemDeMovimentacoes): PaginacaoDaListagem | undefined =>
  usarArmazenamentoLocal<PaginacaoDaListagem | undefined>(
    (armazenamento) => converterParaPaginacao(armazenamento.getItem(chaveDaListagem(listagem))),
    undefined
  );

/**
 * Acrescenta ao filtro da exportação a mesma paginação da listagem, para que o arquivo
 * traga exatamente as linhas que o participante está vendo na tela.
 *
 * Sem essa paginação o backend ignora o limite e devolve todos os registros do filtro.
 * Quando não há paginação registrada, o filtro é devolvido intacto.
 */
export const aplicarPaginacaoDaListagem = <T extends FiltroComPaginacao>(
  listagem: ListagemDeMovimentacoes,
  filtro?: T
): T | undefined => {
  const paginacao = obterPaginacaoDaListagem(listagem);

  if (paginacao === undefined) {
    return filtro;
  }

  return { ...(filtro ?? ({} as T)), page: paginacao.pagina, pageSize: paginacao.tamanhoDaPagina };
};
